import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../api/apiClient';
import { extractErrorMessage } from '../../api/extractErrorMessage';

const base = '/trades';

export const fetchTradeNotes = createAsyncThunk(
  'tradeNotes/fetch',
  async ({ tradeId, cursor = null, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`${base}/${tradeId}/notes`, {
        params: {
          cursor: cursor || undefined,
          limit,
        },
      });
      return res.data; // { notes, cursor }
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const createTradeNote = createAsyncThunk(
  'tradeNotes/create',
  async ({ tradeId, payload }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`${base}/${tradeId}/notes`, payload);
      return res.data.note;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to create note'));
    }
  }
);

export const updateTradeNote = createAsyncThunk(
  'tradeNotes/update',
  async ({ tradeId, noteId, payload }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`${base}/${tradeId}/notes/${noteId}`, payload);
      return res.data.note;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to update note'));
    }
  }
);

export const deleteTradeNote = createAsyncThunk(
  'tradeNotes/delete',
  async ({ tradeId, noteId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${base}/${tradeId}/notes/${noteId}`);
      return { noteId };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to delete note'));
    }
  }
);

const initialState = {
  notesByTrade: {},
  loadingByTrade: {},
  loadingMoreByTrade: {},
  errorByTrade: {},
  creating: false,
  createError: null,
  updatingByNote: {},
  updateErrorByNote: {},
  deletingByNote: {},
  deleteErrorByNote: {},
};

const ensureTradeEntry = (state, tradeId) => {
  if (!state.notesByTrade[tradeId]) {
    state.notesByTrade[tradeId] = { items: [], cursor: null };
  }
};

const sortAndDedupNotes = (items = []) => {
  const seen = new Set();
  const result = [];
  items.forEach((note) => {
    if (!note || !note.noteId || seen.has(note.noteId)) {
      return;
    }
    seen.add(note.noteId);
    result.push(note);
  });
  result.sort((a, b) => {
    const aTime = a?.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b?.createdAt ? Date.parse(b.createdAt) : 0;
    return bTime - aTime;
  });
  return result;
};

const tradeNotesSlice = createSlice({
  name: 'tradeNotes',
  initialState,
  reducers: {
    clearTradeNotes(state, action) {
      const { tradeId } = action.payload || {};
      if (tradeId) {
        delete state.notesByTrade[tradeId];
        delete state.loadingByTrade[tradeId];
        delete state.loadingMoreByTrade[tradeId];
        delete state.errorByTrade[tradeId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTradeNotes.pending, (state, action) => {
        const { tradeId, append = false } = action.meta.arg || {};
        if (!tradeId) { return; }
        ensureTradeEntry(state, tradeId);
        state.errorByTrade[tradeId] = null;
        if (append) {
          state.loadingMoreByTrade[tradeId] = true;
        } else {
          state.loadingByTrade[tradeId] = true;
        }
      })
      .addCase(fetchTradeNotes.fulfilled, (state, action) => {
        const { tradeId, append = false } = action.meta.arg || {};
        if (!tradeId) { return; }
        ensureTradeEntry(state, tradeId);
        const { notes = [], cursor = null } = action.payload || {};
        const existing = append ? state.notesByTrade[tradeId].items : [];
        const combined = append ? [...existing, ...notes] : notes;
        state.notesByTrade[tradeId] = {
          items: sortAndDedupNotes(combined),
          cursor,
        };
        state.loadingByTrade[tradeId] = false;
        state.loadingMoreByTrade[tradeId] = false;
      })
      .addCase(fetchTradeNotes.rejected, (state, action) => {
        const { tradeId, append = false } = action.meta.arg || {};
        if (!tradeId) { return; }
        state.errorByTrade[tradeId] = action.payload || action.error?.message || 'Failed to load notes';
        state.loadingByTrade[tradeId] = false;
        state.loadingMoreByTrade[tradeId] = false;
      })

      .addCase(createTradeNote.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createTradeNote.fulfilled, (state, action) => {
        const { tradeId } = action.meta.arg || {};
        if (tradeId) {
          ensureTradeEntry(state, tradeId);
          state.notesByTrade[tradeId].items = sortAndDedupNotes([
            action.payload,
            ...state.notesByTrade[tradeId].items,
          ]);
        }
        state.creating = false;
      })
      .addCase(createTradeNote.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload || action.error?.message || 'Failed to create note';
      })

      .addCase(updateTradeNote.pending, (state, action) => {
        const { noteId } = action.meta.arg || {};
        if (!noteId) { return; }
        state.updatingByNote[noteId] = true;
        state.updateErrorByNote[noteId] = null;
      })
      .addCase(updateTradeNote.fulfilled, (state, action) => {
        const { tradeId, noteId } = action.meta.arg || {};
        if (!tradeId || !noteId) { return; }
        ensureTradeEntry(state, tradeId);
        const items = state.notesByTrade[tradeId].items.map((note) =>
          note.noteId === noteId ? action.payload : note
        );
        state.notesByTrade[tradeId].items = sortAndDedupNotes(items);
        state.updatingByNote[noteId] = false;
      })
      .addCase(updateTradeNote.rejected, (state, action) => {
        const { noteId } = action.meta.arg || {};
        if (!noteId) { return; }
        state.updatingByNote[noteId] = false;
        state.updateErrorByNote[noteId] = action.payload || action.error?.message || 'Failed to update note';
      })

      .addCase(deleteTradeNote.pending, (state, action) => {
        const { noteId } = action.meta.arg || {};
        if (!noteId) { return; }
        state.deletingByNote[noteId] = true;
        state.deleteErrorByNote[noteId] = null;
      })
      .addCase(deleteTradeNote.fulfilled, (state, action) => {
        const { tradeId, noteId } = action.meta.arg || {};
        if (!tradeId || !noteId) { return; }
        ensureTradeEntry(state, tradeId);
        state.notesByTrade[tradeId].items = state.notesByTrade[tradeId].items.filter(
          (note) => note.noteId !== noteId
        );
        state.deletingByNote[noteId] = false;
      })
      .addCase(deleteTradeNote.rejected, (state, action) => {
        const { noteId } = action.meta.arg || {};
        if (!noteId) { return; }
        state.deletingByNote[noteId] = false;
        state.deleteErrorByNote[noteId] = action.payload || action.error?.message || 'Failed to delete note';
      });
  },
});

export const { clearTradeNotes } = tradeNotesSlice.actions;
export default tradeNotesSlice.reducer;
